import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { ProductCard } from "@/components/ProductCard";
import { SEO } from "@/components/SEO";
import { AdSlot } from "@/components/AdSlot";

function ProductGrid({ products }) {
  if (!products.length) return null;
  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
      {products.map((p) => <ProductCard key={p.id} product={p} />)}
    </div>
  );
}

export function CategoryPage() {
  const { name } = useParams();
  const decoded = decodeURIComponent(name);
  const [products, setProducts] = useState([]);
  const [category, setCategory] = useState(null);

  useEffect(() => {
    api.get("/products", { params: { category: decoded, limit: 60 } }).then((r) => setProducts(r.data));
    api.get("/categories").then((r) => {
      const found = r.data.find((c) => c.name === decoded);
      setCategory(found || { name: decoded, description: "", image_url: "" });
    });
  }, [decoded]);

  const splitAt = Math.min(8, Math.ceil(products.length / 2));

  return (
    <div>
      <SEO
        title={`Best ${decoded} on Amazon.in`}
        description={`Editor-picked ${decoded.toLowerCase()} available on Amazon.in — with pros, cons, ratings, and honest reviews.`}
        image={category?.image_url}
        path={`/category/${encodeURIComponent(decoded)}`}
      />
      <section className="relative overflow-hidden">
        {category?.image_url ? (
          <div className="absolute inset-0">
            <img src={category.image_url} alt="" className="w-full h-full object-cover opacity-30" />
            <div className="absolute inset-0 bg-gradient-to-b from-[#F9F8F6]/60 via-[#F9F8F6]/85 to-[#F9F8F6]" />
          </div>
        ) : null}
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 py-20">
          <p className="text-xs uppercase tracking-[0.2em] text-[#5B7B53] mb-3">Category</p>
          <h1 className="font-display text-5xl sm:text-6xl text-[#2C2B29]">{decoded}</h1>
          {category?.description ? (
            <p className="mt-4 text-lg text-[#5C5A56] max-w-2xl">{category.description}</p>
          ) : null}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-20">
        {products.length === 0 ? (
          <p data-testid="empty-state" className="text-[#5C5A56]">No products in this category yet.</p>
        ) : (
          <>
            <ProductGrid products={products.slice(0, splitAt)} />
            <AdSlot key={decoded} type="display" testid="category-ad" />
            <ProductGrid products={products.slice(splitAt)} />
          </>
        )}
      </section>
    </div>
  );
}

export function SearchPage() {
  const [params] = useSearchParams();
  const q = params.get("q") || "";
  const [products, setProducts] = useState([]);

  useEffect(() => {
    if (q) api.get("/products", { params: { search: q, limit: 60 } }).then((r) => setProducts(r.data));
  }, [q]);

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16">
      <SEO title={`Search: ${q}`} description={`Search results for "${q}" across HomeHearth's curated Amazon.in picks.`} path={`/search?q=${encodeURIComponent(q)}`} />
      <p className="text-xs uppercase tracking-[0.2em] text-[#5B7B53] mb-3">Search results</p>
      <h1 className="font-display text-4xl text-[#2C2B29] mb-10">"{q}"</h1>
      {products.length === 0 ? (
        <p className="text-[#5C5A56]">No matches. Try different keywords.</p>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {products.map((p) => (
            <ProductCard key={p.id} product={p} />
          ))}
        </div>
      )}
    </div>
  );
}
